import { useState, useEffect, useRef } from 'react';
import { Search, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AI_COURSE_IMAGES } from '../../config/courseImages';

interface Course {
  id: number;
  title: string;
  description: string;
  image: string;
  category: string;
  level: string;
  udemyUrl?: string;
}

interface SearchDropdownProps {
  className?: string;
}

const SearchDropdown = ({ className }: SearchDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  // Combined course data - includes both AI and Automation courses
  const courses: Course[] = [
    // AI Courses
    {
      id: 8,
      title: 'Red Teaming AI Apps with Promptfoo: RAG, Chatbots & Agents',
      description: 'Learn practical AI security testing with Promptfoo for RAG apps, chatbots, and AI agents, including jailbreaks, prompt injection, PII leakage, and OWASP risks.',
      image: AI_COURSE_IMAGES['ai-red-teaming-promptfoo-rag-chatbots-agents'],
      category: 'ai-testing',
      level: 'Beginner',
      udemyUrl: 'https://www.udemy.com/course/ai-red-teaming-promptfoo-rag-chatbots-agents/',
    },
    {
      id: 9,
      title: 'ZERO Code AI Tests: Automate Software Tests in Plain English',
      description: 'Automate software tests without writing code by describing tests in plain English and letting AI generate, execute, and maintain them.',
      image: AI_COURSE_IMAGES['zero-code-ai-testing'],
      category: 'ai-testing',
      level: 'Beginner',
      udemyUrl: 'https://www.udemy.com/course/zero-code-ai-testing/',
    },
    {
      id: 1,
      title: 'Using Gen AI & AI Agent in Software Automation Testing',
      description: 'Start your AI testing journey with fundamentals of GenAI, prompt engineering, AI Agents, MCP server usage, making Selenium and Playwright test automation smarter with AI.',
      image: AI_COURSE_IMAGES['generative-ai-in-software-automation-testing'],
      category: 'ai-testing',
      level: 'Beginner',
      udemyUrl: 'https://www.udemy.com/course/generative-ai-in-software-automation-testing/',
    },
    {
      id: 2,
      title: 'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
      description: 'Learn to test AI applications using specialized frameworks like DeepEval, RAGAs, HuggingFace Evaluate, Confident AI along with using Ollama with Local LLMs for comprehensive AI validation.',
      image: AI_COURSE_IMAGES['ai-testing-deepeval-ragas-ollama'],
      category: 'ai-testing',
      level: 'Intermediate',
      udemyUrl: 'https://www.udemy.com/course/ai-testing-deepeval-ragas-ollama/',
    },
    {
      id: 3,
      title: 'LangChain for Beginners : Building AI Agents, RAG & ChatBots',
      description: 'Master building and testing AI agents, chatbots, and RAG systems using AutoGen, LangChain with Ollama and local LLMs.',
      image: AI_COURSE_IMAGES['build-ai-agent-chatbot-rag-langchain-local-llm'],
      category: 'ai-development',
      level: 'Intermediate',
      udemyUrl: 'https://www.udemy.com/course/build-ai-agent-chatbot-rag-langchain-local-llm/',
    },
    {
      id: 4,
      title: 'Build Agentic AI & Multi-Agent System for Software Testing',
      description: 'Learn Building and Testing AI Agent, ChatBot, RAG with LangChain and LangSmith using Ollama and Local LLMs and RAGAs.',
      image: AI_COURSE_IMAGES['genai-multi-agent-software-testing'],
      category: 'ai-testing',
      level: 'Advanced',
      udemyUrl: 'https://www.udemy.com/course/genai-multi-agent-software-testing/',
    },
    {
      id: 5,
      title: 'LLM Fine-Tuning for Beginners: HuggingFace & Unsloth',
      description: 'Understand Transformer models and learn how to Test, Evaluate, and Train them with HuggingFace NLP library.',
      image: AI_COURSE_IMAGES['ai-with-huggingface'],
      category: 'ai-development',
      level: 'Expert',
      udemyUrl: 'https://www.udemy.com/course/ai-with-huggingface/',
    },
    {
      id: 10,
      title: 'E2E Testing ChatBot, AI Agent, RAG, MCP Server with DeepEval',
      description: 'Build an end-to-end testing strategy for chatbots, AI agents, RAG pipelines, and MCP servers using DeepEval.',
      image: AI_COURSE_IMAGES['ai-testing-deepeval-chatbot-rag-agent-mcp'],
      category: 'ai-testing',
      level: 'Intermediate',
      udemyUrl: 'https://www.udemy.com/course/ai-testing-deepeval-chatbot-rag-agent-mcp/',
    },
    {
      id: 11,
      title: 'AI-Driven Test Automation: Playwright, Selenium, LLMs & More',
      description: 'Build resilient test automation with AI-powered self-healing locators, Selenium, Playwright, Ollama, and local LLMs.',
      image: AI_COURSE_IMAGES['ai-test-automation-selenium-playwright-llms'],
      category: 'ai-testing',
      level: 'Intermediate',
      udemyUrl: 'https://www.udemy.com/course/ai-test-automation-selenium-playwright-llms/',
    },
    // Automation Courses
    {
      id: 101,
      title: 'Automation Framework Development with Playwright in C# .NET',
      description: 'Master building robust, scalable test automation frameworks using Playwright with C# .NET. Learn advanced framework architecture, design patterns, reporting, and CI/CD integration.',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=240&fit=crop',
      category: 'automation',
      level: 'Advanced',
      udemyUrl: 'https://www.udemy.com/course/framework-development-with-playwright-dotnet/',
    },
    {
      id: 102,
      title: 'API Testing with RestSharp along with Framework Development',
      description: 'Comprehensive API testing using RestSharp in C#. Learn to build API automation frameworks, validate REST services, handle authentication, and integrate with BDD using SpecFlow.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=240&fit=crop',
      category: 'automation',
      level: 'Intermediate',
      udemyUrl: 'https://www.udemy.com/course/api-testing-with-restsharp-and-specflow-in-csharp/',
    },
    {
      id: 103,
      title: 'End to End Test Automation with Playwright (TS/C#/Java)',
      description: 'Complete guide to end-to-end test automation using Playwright across multiple programming languages. Master web testing, mobile testing, and cross-browser automation.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=240&fit=crop',
      category: 'automation',
      level: 'Intermediate',
      udemyUrl: 'https://www.udemy.com/course/e2e-playwright/',
    },
    {
      id: 104,
      title: 'Automation Framework Development with Selenium C# .NET',
      description: 'Advanced Selenium automation framework development using C# .NET. Build enterprise-ready frameworks with design patterns, parallel execution, and comprehensive reporting.',
      image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=240&fit=crop',
      category: 'automation',
      level: 'Advanced',
      udemyUrl: 'https://www.udemy.com/course/framework-development-with-selenium-csharp-advanced/',
    },
    {
      id: 105,
      title: 'Selenium with C# .NET for Absolute Beginners',
      description: 'Complete beginner-friendly course on Selenium automation using C# .NET. Learn web automation fundamentals, basic framework concepts, and essential testing practices.',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=240&fit=crop',
      category: 'automation',
      level: 'Beginner',
      udemyUrl: 'https://www.udemy.com/course/selenium-dotnet-basics/',
    },
    {
      id: 106,
      title: 'Spring Boot for Automation Testing-UI and Microservice',
      description: 'Comprehensive Spring Boot testing covering UI automation, microservice testing, and integration strategies. Learn to build robust testing solutions in Java ecosystem.',
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=240&fit=crop',
      category: 'automation',
      level: 'Intermediate',
      udemyUrl: 'https://www.udemy.com/course/spring-boot-testing/',
    }
  ];

  // Filter courses based on search term
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = courses.filter((course) =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCourses(filtered.slice(0, 5)); // Show max 5 results
      setIsOpen(true);
    } else {
      setFilteredCourses([]);
      setIsOpen(false);
    }
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Determine if the search matches more AI courses or Automation courses
      const aiCourses = courses.filter(c => ['ai-testing', 'ai-development', 'genai'].includes(c.category));
      const automationCourses = courses.filter(c => c.category === 'automation');
      
      const aiMatches = aiCourses.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      const automationMatches = automationCourses.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // Navigate to the page with more matches, default to AI courses
      if (automationMatches.length > aiMatches.length) {
        navigate(`/automation?search=${encodeURIComponent(searchTerm.trim())}`);
      } else {
        navigate(`/courses?search=${encodeURIComponent(searchTerm.trim())}`);
      }
      
      setSearchTerm('');
      setIsOpen(false);
    }
  };

  const handleCourseClick = (course: Course) => {
    if (course.udemyUrl) {
      window.open(course.udemyUrl, '_blank');
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Beginner':
        return 'bg-green-100 text-green-800';
      case 'Intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'Advanced':
        return 'bg-purple-100 text-purple-800';
      case 'Expert':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <form onSubmit={handleSearch} className="relative">
        <div className="relative group">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-primary-500 transition-colors" aria-label="Search icon" />
          <input
            type="text"
            placeholder="Search AI & Automation courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => searchTerm.trim() && setIsOpen(true)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm hover:border-gray-300 transition-all duration-300"
          />
        </div>
      </form>

      {/* Search Results Dropdown */}
      {isOpen && filteredCourses.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-[1001] max-h-96 overflow-y-auto">
          <div className="p-2">
            <div className="text-xs text-gray-500 px-3 py-2 font-medium">
              {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''} found
            </div>
            {filteredCourses.map((course) => (
              <div
                key={course.id}
                onClick={() => handleCourseClick(course)}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors group"
              >
                <img
                  src={course.image}
                  alt={course.title}
                  loading="lazy"
                  className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-1 group-hover:text-green-600 transition-colors">
                      {course.title}
                    </h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${getLevelColor(course.level)}`}>
                      {course.level}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {course.description}
                  </p>
                </div>
                <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-green-500 transition-colors flex-shrink-0" aria-label="ExternalLink icon" />
              </div>
            ))}
            <div className="border-t border-gray-100 mt-2 pt-2">
              <button
                onClick={handleSearch}
                className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors font-medium"
              >
                View all results for "{searchTerm}"
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No Results */}
      {isOpen && searchTerm.trim() && filteredCourses.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-[1001]">
          <div className="p-4 text-center text-gray-500">
            <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" aria-label="Search icon" />
            <p className="text-sm">No courses found for "{searchTerm}"</p>
            <button
              onClick={handleSearch}
              className="text-green-600 hover:text-green-700 text-sm font-medium mt-1"
            >
              Search all courses
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
